import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, SlidersHorizontal, Loader2, PackageSearch, X } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { ProductShowcase } from "@/components/shop/ProductShowcase";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCard } from "@/components/shop/ProductCard";
import { PurchaseOptionDialog } from "@/components/shop/PurchaseOptionDialog";
import { ShopFilters, emptyFilters, type Filters } from "@/components/shop/ShopFilters";
import { ProductRail } from "@/components/shop/ProductRail";
import { listProducts } from "@/lib/shop.functions";
import { buildShopGroups, CATEGORIES, SORT_OPTIONS, searchMatches, standardDisplayPrice, type Product, type SortValue } from "@/lib/shop";
import { useCompare, useRecentlyViewed, useWishlist } from "@/lib/shop-prefs";
import { SearchSuggest } from "@/components/shop/SearchSuggest";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export const Route = createFileRoute("/shop/")({
  component: Shop,
  head: () => ({
    meta: [
      { title: "Rehabilitation Equipment Shop | N S Physiotherapy" },
      {
        name: "description",
        content:
          "Shop physiotherapist selected braces, supports, mobility aids and recovery products from N S Physiotherapy in Centurion, with expert guidance.",
      },
      { property: "og:title", content: "Rehabilitation Equipment Shop | N S Physiotherapy" },
      {
        property: "og:description",
        content: "Shop quality braces, supports, mobility aids and recovery products selected by qualified physiotherapists.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://nsphysiotherapy.co.za/shop" }],
  }),
});

function Shop() {
  const fn = useServerFn(listProducts);
  const { data, isPending, isError } = useQuery({ queryKey: ["shop", "products"], queryFn: () => fn() });
  const products = (data ?? []) as Product[];

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortValue>("featured");
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [selected, setSelected] = useState<Product | null>(null);
  const wishlist = useWishlist();
  const compare = useCompare();
  const recent = useRecentlyViewed();

  const facets = useMemo(() => {
    const brands = new Set<string>();
    const sizes = new Set<string>();
    const colours = new Set<string>();
    let priceMax = 1000;
    for (const p of products) {
      if (p.brand) brands.add(p.brand);
      p.sizes?.forEach((s) => sizes.add(s));
      p.colours?.forEach((c) => colours.add(c));
      priceMax = Math.max(priceMax, p.standard_price);
    }
    return {
      brands: [...brands].sort(),
      sizes: [...sizes].sort(),
      colours: [...colours].sort(),
      priceMax: Math.ceil(priceMax / 50) * 50,
    };
  }, [products]);

  const results = useMemo(() => {
    let list = products.filter((p) => searchMatches(p, query));
    const f = filters;
    if (f.categories.length)
      list = list.filter((p) => f.categories.includes(p.category ?? "") || f.categories.includes(p.subcategory ?? ""));
    if (f.brands.length) list = list.filter((p) => f.brands.includes(p.brand ?? ""));
    if (f.sizes.length) list = list.filter((p) => p.sizes?.some((s) => f.sizes.includes(s)));
    if (f.colours.length) list = list.filter((p) => p.colours?.some((c) => f.colours.includes(c)));
    if (f.sides.length) list = list.filter((p) => p.sides?.some((s) => f.sides.includes(s)));
    if (f.inStock) list = list.filter((p) => p.stock_quantity > 0);
    if (f.onSale) list = list.filter((p) => p.on_sale);
    if (f.physioRecommended) list = list.filter((p) => p.physio_recommended);
    if (f.maxPrice != null) list = list.filter((p) => standardDisplayPrice(p) <= f.maxPrice!);

    const sorted = [...list];
    switch (sort) {
      case "newest":
        sorted.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
        break;
      case "best-selling":
        sorted.sort((a, b) => (b.units_sold ?? 0) - (a.units_sold ?? 0) || a.sort_order - b.sort_order);
        break;
      case "popular":
        sorted.sort((a, b) => (b.view_count ?? 0) - (a.view_count ?? 0) || (b.units_sold ?? 0) - (a.units_sold ?? 0));
        break;
      case "rating":
        sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || (b.rating_count ?? 0) - (a.rating_count ?? 0));
        break;
      case "price-asc":
        sorted.sort((a, b) => standardDisplayPrice(a) - standardDisplayPrice(b));
        break;
      case "price-desc":
        sorted.sort((a, b) => standardDisplayPrice(b) - standardDisplayPrice(a));
        break;
      case "name-asc":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        sorted.sort((a, b) => Number(b.featured) - Number(a.featured) || a.sort_order - b.sort_order);
    }
    return sorted;
  }, [products, query, filters, sort]);

  const popular = useMemo(
    () =>
      [...products]
        .sort((a, b) => (b.units_sold ?? 0) - (a.units_sold ?? 0) || Number(b.featured) - Number(a.featured))
        .slice(0, 6),
    [products],
  );

  const recentlyViewed = useMemo(
    () => recent.list.map((id) => products.find((p) => p.id === id)).filter((p): p is Product => !!p).slice(0, 8),
    [recent.list, products],
  );


  const groups = useMemo(() => buildShopGroups(products), [products]);
  const isBrowsing =
    !query.trim() &&
    filters.categories.length === 0 &&
    filters.brands.length === 0 &&
    filters.sizes.length === 0 &&
    filters.colours.length === 0 &&
    filters.sides.length === 0 &&
    !filters.inStock &&
    !filters.onSale &&
    !filters.physioRecommended &&
    filters.maxPrice == null &&
    sort === "featured";

  const filterPanel = <ShopFilters filters={filters} setFilters={setFilters} facets={facets} />;

  return (
    <PageShell>
      <section className="border-b border-border bg-background">
        <div className="container-page grid items-center gap-6 py-6 md:grid-cols-[1.1fr_1fr] md:py-10">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">Physio Shop</div>
            <h1 className="mt-1.5 font-display text-2xl font-bold text-primary md:text-4xl">Rehabilitation Equipment</h1>
            <p className="mt-2 max-w-xl text-sm text-foreground/70 md:text-base">
              Chosen by physiotherapists. Shop quality braces, supports, mobility aids and recovery equipment with
              optional physiotherapist guidance.
            </p>
            <a
              href="#shop-products"
              className="btn-hero mt-4 inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold"
            >
              Shop All Products
            </a>
          </div>
          <ProductShowcase compact />
        </div>
      </section>

      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Shop" }]} />


      <div className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="container-page py-3">
          <SearchSuggest products={products} query={query} setQuery={setQuery} popular={popular} />


          <nav aria-label="Product categories" className="mt-3 flex gap-2 overflow-x-auto pb-1">
            <CategoryChip
              label="All"
              active={filters.categories.length === 0}
              onClick={() => setFilters({ ...filters, categories: [] })}
            />
            {CATEGORIES.map((c) => (
              <CategoryChip
                key={c.name}
                label={c.name}
                active={filters.categories.includes(c.name)}
                onClick={() => setFilters({ ...filters, categories: [c.name] })}
              />
            ))}
          </nav>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {isPending ? "Loading" : `${results.length} product${results.length === 1 ? "" : "s"}`}
            </p>
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <button className="inline-flex h-9 items-center gap-2 rounded-full border border-border px-4 text-xs font-semibold text-primary hover:bg-accent lg:hidden">
                    <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] overflow-y-auto sm:max-w-sm">
                  <SheetHeader>
                    <SheetTitle className="font-display text-primary">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">{filterPanel}</div>
                </SheetContent>
              </Sheet>
              <label className="sr-only" htmlFor="sort">
                Sort products
              </label>
              <select
                id="sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortValue)}
                className="h-9 rounded-full border border-border bg-background px-3 text-xs font-semibold text-primary outline-none focus:border-primary"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <section id="shop-products" className="container-page scroll-smooth py-8">
        <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-44 max-h-[calc(100dvh-13rem)] overflow-y-auto pr-2">{filterPanel}</div>
          </aside>

          <div className="min-w-0">
            {isPending ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <p className="text-sm text-destructive">We could not load the catalogue. Please refresh and try again.</p>
            ) : products.length === 0 ? (
              <EmptyCatalogue />
            ) : results.length === 0 ? (
              <div className="rounded-2xl border border-border bg-secondary/30 p-10 text-center">
                <PackageSearch className="mx-auto h-10 w-10 text-muted-foreground/60" />
                <h2 className="mt-4 font-display text-lg font-semibold text-primary">No matching products</h2>
                <p className="mt-2 text-sm text-muted-foreground">Try a different search term or clear your filters.</p>
              </div>
            ) : isBrowsing ? (
              <div className="divide-y divide-border">
                {groups.map((g) => (
                  <ProductRail
                    key={g.slug}
                    title={g.name}
                    subtitle={g.subtitle}
                    slug={g.slug}
                    products={g.products}
                    onAdd={setSelected}
                    wishlist={wishlist}
                    compare={compare}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAdd={setSelected}
                    wishlisted={wishlist.has(p.id)}
                    onWishlist={wishlist.toggle}
                    compared={compare.has(p.id)}
                    onCompare={compare.toggle}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {recentlyViewed.length > 0 && (
        <section className="border-t border-border">
          <div className="container-page">
            <ProductRail
              title="Recently viewed"
              subtitle="Pick up where you left off"
              products={recentlyViewed}
              onAdd={setSelected}
              wishlist={wishlist}
              compare={compare}
            />
          </div>
        </section>
      )}

      <PurchaseOptionDialog product={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
    </PageShell>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
        active ? "border-primary bg-primary text-primary-foreground" : "border-border text-primary hover:bg-accent"
      }`}
    >
      {label}
    </button>
  );
}

function EmptyCatalogue() {
  return (
    <div className="rounded-2xl border border-border bg-secondary/30 p-10 text-center">
      <PackageSearch className="mx-auto h-12 w-12 text-primary" />
      <h2 className="mt-4 font-display text-xl font-bold text-primary">Catalogue coming soon</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
        Our rehabilitation equipment catalogue is currently being prepared. New physiotherapist recommended products will
        be added soon.
      </p>
    </div>
  );
}
